import React, { useState } from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'
import formDataEntries from 'form-data-entries'
import Form from 'react-netlify-form'
import Button from 'components/button/button'
import { Submit } from 'components/forms/elements/v2/submit'
import ResponseMessage from 'components/forms/elements/response-message'
import { clearfix } from 'styles/clearfix'
import { useEffectOnce } from 'hooks/use-effect-once'
import { useFormValidation } from 'hooks/use-form-validation'
import { track } from 'utils/amplitude'

let isSubscribed = false

const MultiStepForm = ({
  buttonClass,
  className,
  functionName,
  name,
  steps,
  ...additionalProps
}) => {
  const formName = `${name} Form`
  const lastPage = steps.length

  const [page, setPage] = useState(1)
  const [showNextButton, setShowNextButton] = useState(true)
  const [showPrevButton, setShowPrevButton] = useState(false)
  const [showSubmit, setShowSubmit] = useState(false)
  const [valid, updateValidationSet] = useFormValidation({})
  const [responseData, setResponseData] = useState({
    error: false,
    message: '',
    messageBody: '',
    success: false,
  })

  useEffectOnce(() => {
    isSubscribed = true

    if (/schedule.service/i.test(name)) {
      track('Service Request Started', {
        'form name': formName,
        'service category': '',
        'equipment type': '',
        'location id': '',
        'lead source': 'schedule service page',
      })
    }

    return () => {
      isSubscribed = false
    }
  })

  const renderStepComponents = () => {
    return steps.map((step, i) => {
      let className = 'inactive'
      if (i + 1 === page) {
        className = 'active'
      } else if (i < page) {
        className = 'completed'
      }
      return (
        <ProgressBarItem className={className} onClick={() => goToPage(i + 1)} key={i}>
          <p>{i + 1}</p>
          <span>{step.name}</span>
        </ProgressBarItem>
      )
    })
  }

  const renderComponents = () => {
    return steps.map((step, i) => {
      return (
        <div key={i} style={page - 1 === i ? {} : { display: 'none' }}>
          {step.renderComponent({ updateValidationSet, ...additionalProps })}
        </div>
      )
    })
  }

  const goToPage = newPage => {
    if (newPage === page) {
      return
    }

    setPage(newPage)
    setShowSubmit(newPage === lastPage)
    setShowNextButton(newPage !== lastPage)
    setShowPrevButton(newPage !== 1)
  }

  const next = () => {
    const nextPage = page + 1

    goToPage(nextPage)
  }

  const previous = () => {
    const prevPage = page - 1

    goToPage(prevPage)
  }

  const onSubmitForm = () => {
    if (!functionName) {
      setResponseData({
        error: false,
        message: 'Success!',
        messageBody: 'Your form has been submitted. Our team will be in touch with you soon!',
        success: true,
      })

      if (/schedule.service/i.test(name)) {
        track('Service Request Submitted', {
          'service request id': '',
          'service category': '',
          'equipment type': '',
          'preferred appointment window': '',
          'location id': '',
          'is emergency': false,
          'lead source': 'schedule service page',
        })

        track('Lead Submitted', {
          'lead id': '',
          'lead type': 'service request',
          'form name': formName,
          'lead source': 'schedule service page',
          'location id': '',
          'equipment id': '',
          'preferred contact method': '',
        })
      }
      return
    }
    const formData = {}
    for (const [name, value] of formDataEntries(document.forms[formName])) {
      formData[name] = value
    }
    formData['pageName'] = document.title

    fetch(`/api/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(formData),
    })
      .then(res => {
        if (isSubscribed) {
          const ok = res.ok

          setResponseData({
            error: !ok,
            message: ok ? 'Success!' : 'Error.',
            messageBody: ok
              ? 'Your form has been submitted. Our team will be in touch with you soon!'
              : 'There was an issue submitting your form. If problems persist please contact <a href="mailto:contactus@hutsoninc.com">our support team</a>.',
            success: ok,
          })

          if (ok && /schedule.service/i.test(name)) {
            track('Service Request Submitted', {
              'service request id': '',
              'service category': '',
              'equipment type': '',
              'preferred appointment window': '',
              'location id': '',
              'is emergency': false,
              'lead source': 'schedule service page',
            })

            track('Lead Submitted', {
              'lead id': '',
              'lead type': 'service request',
              'form name': formName,
              'lead source': 'schedule service page',
              'location id': '',
              'equipment id': '',
              'preferred contact method': '',
            })
          }
        }
      })
      .catch(err => {
        if (isSubscribed) {
          console.error('Error submitting form: ', err)
          setResponseData({
            error: true,
            message: 'Error.',
            messageBody:
              'There was an issue submitting your form. If problems persist please contact <a href="mailto:contactus@hutsoninc.com">our support team</a>.',
            success: false,
          })
        }
      })
  }
  return (
    <Form name={formName} onSubmit={onSubmitForm} className={className}>
      {({ error, loading, success }) => (
        <div>
          {success || responseData.success ? (
            <ResponseMessage
              message={responseData.message}
              messageBody={responseData.messageBody}
            />
          ) : (
            <>
              <>
                {error && (
                  <ResponseMessage
                    error={true}
                    message='Error.'
                    messageBody='There was an issue submitting your form. If problems persist please contact <a href="mailto:contactus@hutsoninc.com">our support team</a>.'
                  />
                )}
                {responseData.error && !error && (
                  <ResponseMessage
                    error={responseData.error}
                    message={responseData.message}
                    messageBody={responseData.messageBody}
                  />
                )}
              </>
            </>
          )}
          {!(success || responseData.success) ? (
            <Container>
              <ProgressBar>{renderStepComponents()}</ProgressBar>
              <div className='fields'>{renderComponents()}</div>
              <Navigation>
                <div className='submit' style={showSubmit ? {} : { display: 'none' }}>
                  <StyledSubmit
                    className={`${name.split(' ').join('-')}-Form ${buttonClass}`}
                    disabled={!valid || (success && responseData.success)}
                    loading={loading}
                  />
                </div>
                <div
                  className='navigation-button navigation-button-next'
                  style={showNextButton ? {} : { display: 'none' }}
                >
                  <StyledButton onClick={next}>Next</StyledButton>
                </div>
                <div
                  className='navigation-button navigation-button-previous'
                  style={showPrevButton ? {} : { display: 'none' }}
                >
                  <StyledButton color='gray' onClick={previous}>
                    Previous
                  </StyledButton>
                </div>
              </Navigation>
            </Container>
          ) : null}
        </div>
      )}
    </Form>
  )
}

MultiStepForm.propTypes = {
  buttonClass: PropTypes.string,
  className: PropTypes.string,
  functionName: PropTypes.string,
  name: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      renderComponent: PropTypes.func.isRequired,
    })
  ).isRequired,
}

MultiStepForm.defaultProps = {
  buttonClass: '',
  className: '',
}

const ProgressBar = styled.ol`
  display: block;
  list-style: none;
  margin: 0 auto 20px;
  padding: 20px 0;
  text-align: left;
`

const ProgressBarItemLinesBefore = `
    background-color: #f6f6f6;
    content: "";
    cursor: default;
    display: inline-block;
    height: 4px;
    position: absolute;
    right: 50%;
    top: 12px;
    width: 100%;
    z-index: 0;
`

const ProgressBarItemLinesAfter = `
    background-color: #f6f6f6;
    content: "";
    cursor: default;
    display: inline-block;
    height: 4px;
    position: absolute;
    top: 12px;
    width: 100%;
    z-index: 0;
`

const ProgressBarItem = styled.li`
  display: inline-block;
  margin: 0 8px;
  padding: 0;
  text-align: center;

  p {
    cursor: pointer;
    display: inline-block;
    margin: 0;
    padding: 5px 10px;
  }

  span {
    cursor: pointer;
    display: none;
    font-size: 0.8em;
    margin-top: 10px;
  }

  &.completed {
    p {
      background-color: #377539;
      color: #fff;
    }
  }

  &.active {
    p {
      background-color: #377539;
      color: #fff;
    }
  }

  &.inactive {
    p {
      background-color: #f6f6f6;
      color: #a7a7a7;
    }
  }

  @media (min-width: 800px) {
    margin: 0 15px;
    position: relative;
    vertical-align: top;

    p {
      position: relative;
      z-index: 1;
    }

    span {
      display: block;
    }

    &.completed {
      :not(:first-child):before {
        ${ProgressBarItemLinesBefore}
        background-color: #377539;
      }

      :after {
        ${ProgressBarItemLinesAfter}
        background-color: #377539;
      }
    }

    &.active {
      :not(:first-child):before {
        ${ProgressBarItemLinesBefore}
        background-color: #377539;
      }

      :not(:last-child):after {
        ${ProgressBarItemLinesAfter}
      }
    }

    &.inactive {
      :not(:first-child):before {
        ${ProgressBarItemLinesBefore}
      }

      :not(:last-child):after {
        ${ProgressBarItemLinesAfter}
      }
    }
  }
`

const Navigation = styled.div`
  display: block;
  float: left;
  width: 100%;

  .navigation-button {
    display: block;
    float: right;
    margin-top: 25px;
    width: 48%;
  }

  .navigation-button-previous {
    margin-right: 4%;
  }

  .button {
    padding: 10px 0;
  }

  .submit {
    display: block;
    float: right;
    margin-top: 25px;
    width: 48%;
  }

  @media (min-width: 800px) {
    float: right;

    .navigation-button {
      width: 200px;
    }

    .navigation-button-previous {
      margin-right: 25px;
    }

    .submit {
      display: block;
      float: right;
      width: 200px;
    }
  }
`

const StyledButton = styled(Button)`
  width: 100%;
`

const StyledSubmit = styled(Submit)`
  width: 100%;
`

const Container = styled.div`
  ${clearfix}
`

export default MultiStepForm
